import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BarChart3Icon,
  TrendingUpIcon,
  CalendarIcon,
  FileTextIcon,
  DownloadIcon,
  DollarSignIcon,
  UsersIcon,
  ClockIcon,
  CheckCircleIcon,
  AlertCircleIcon,
  Loader2Icon,
  WrenchIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { exportRowsToCsv } from '../lib/csvExport'
import { useDivision } from '../contexts/DivisionContext'
import { matchesDivisionWithFallback } from '../lib/divisionFiltering'
import {
  MobilePage,
} from '../components/mobile'

export function Reports() {
  const navigate = useNavigate()
  const { currentDivision } = useDivision()
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [stats, setStats] = useState({
    totalRevenue: 0,
    activeContracts: 0,
    totalCustomers: 0,
    jobsCompletedThisMonth: 0,
  })

  useEffect(() => {
    fetchStats()
  }, [currentDivision])

  const fetchStats = async () => {
    try {
      setLoading(true)
      setError(null)

      const startDate = new Date(dateRange.start)
      const endDate = new Date(dateRange.end)
      endDate.setHours(23, 59, 59, 999)

      const { data: completedJobs } = await supabase
        .from('jobs')
        .select('actual_amount, quoted_amount, actual_end_time, updated_at, operational_division_id, service_type')
        .eq('status', 'completed')

      const totalRevenue = (completedJobs || [])
        .filter((job) => matchesDivisionWithFallback(job, currentDivision))
        .filter((job) => {
          const completionDate = job.actual_end_time || job.updated_at
          if (!completionDate) return false
          const date = new Date(completionDate)
          return date >= startDate && date <= endDate
        })
        .reduce((sum, job) => sum + Number(job.actual_amount || job.quoted_amount || 0), 0)

      let activeContractsCount = 0
      try {
        const { data: activeContracts } = await supabase
          .from('contracts')
          .select('operational_division_id, service_type')
          .in('status', ['active', 'signed', 'in_process'])
        activeContractsCount = (activeContracts || []).filter((contract) =>
          matchesDivisionWithFallback(contract, currentDivision)
        ).length
      } catch { /* contracts table may not exist */ }

      const { count: customersCount } = await supabase
        .from('customers')
        .select('*', { count: 'exact', head: true })

      const now = new Date()
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
      const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999)

      const { data: jobsCompleted } = await supabase
        .from('jobs')
        .select('operational_division_id, service_type')
        .eq('status', 'completed')
        .gte('actual_end_time', firstDayOfMonth.toISOString())
        .lte('actual_end_time', lastDayOfMonth.toISOString())
      const jobsCompletedCount = (jobsCompleted || []).filter((job) =>
        matchesDivisionWithFallback(job, currentDivision)
      ).length

      setStats({
        totalRevenue,
        activeContracts: activeContractsCount,
        totalCustomers: customersCount || 0,
        jobsCompletedThisMonth: jobsCompletedCount,
      })
    } catch (err: any) {
      console.error('Error fetching stats:', err)
      setError(err.message || 'Failed to fetch statistics')
    } finally {
      setLoading(false)
    }
  }

  const handleApplyFilter = () => {
    fetchStats()
  }

  const [exporting, setExporting] = useState<string | null>(null)
  const [exportError, setExportError] = useState<string | null>(null)

  const runExport = async (key: string, fn: () => Promise<void>) => {
    try {
      setExporting(key)
      setExportError(null)
      await fn()
    } catch (err: any) {
      console.error(`Error exporting ${key}:`, err)
      setExportError(err.message || `Failed to export ${key}`)
    } finally {
      setExporting(null)
    }
  }

  const exportRevenueByPeriod = () =>
    runExport('revenue', async () => {
      const { data, error } = await supabase
        .from('invoices')
        .select('invoice_date, total, amount_paid, operational_division_id, contract:contracts(operational_division_id, service_type)')
        .gte('invoice_date', dateRange.start)
        .lte('invoice_date', dateRange.end)
      if (error) throw error

      const byMonth = new Map<string, { invoiced: number; collected: number; count: number }>()
      for (const inv of (data || []).filter((invoice: any) => {
        const contract = Array.isArray(invoice.contract) ? invoice.contract[0] || null : invoice.contract
        return matchesDivisionWithFallback(invoice, currentDivision, contract)
      })) {
        const period = (inv.invoice_date || '').slice(0, 7) || 'unknown'
        const existing = byMonth.get(period) || { invoiced: 0, collected: 0, count: 0 }
        existing.invoiced += Number(inv.total || 0)
        existing.collected += Number(inv.amount_paid || 0)
        existing.count += 1
        byMonth.set(period, existing)
      }
      const rows = Array.from(byMonth.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([period, v]) => ({ period, ...v }))

      exportRowsToCsv(
        `revenue-by-period_${dateRange.start}_${dateRange.end}.csv`,
        [
          { label: 'Period', value: (r) => r.period },
          { label: 'Invoices', value: (r) => r.count },
          { label: 'Invoiced Total', value: (r) => r.invoiced.toFixed(2) },
          { label: 'Collected', value: (r) => r.collected.toFixed(2) },
        ],
        rows
      )
    })

  const exportArAging = () =>
    runExport('ar_aging', async () => {
      const { data, error } = await supabase
        .from('invoices')
        .select('invoice_number, invoice_date, due_date, total, balance_due, customer:customers(company_name)')
        .gt('balance_due', 0)
      if (error) throw error

      const today = new Date()
      const rows = (data || []).map((inv: any) => {
        const due = inv.due_date ? new Date(inv.due_date) : null
        const daysOverdue = due ? Math.floor((today.getTime() - due.getTime()) / 86400000) : 0
        let bucket = 'Current'
        if (daysOverdue > 90) bucket = '90+'
        else if (daysOverdue > 60) bucket = '61-90'
        else if (daysOverdue > 30) bucket = '31-60'
        else if (daysOverdue > 0) bucket = '1-30'
        return {
          invoice_number: inv.invoice_number,
          customer: inv.customer?.company_name || 'Unknown',
          invoice_date: inv.invoice_date,
          due_date: inv.due_date,
          days_overdue: daysOverdue,
          balance_due: Number(inv.balance_due || 0),
          bucket,
        }
      })

      exportRowsToCsv(
        `ar-aging_${new Date().toISOString().split('T')[0]}.csv`,
        [
          { label: 'Invoice #', value: (r) => r.invoice_number },
          { label: 'Customer', value: (r) => r.customer },
          { label: 'Invoice Date', value: (r) => r.invoice_date },
          { label: 'Due Date', value: (r) => r.due_date },
          { label: 'Days Overdue', value: (r) => r.days_overdue },
          { label: 'Balance Due', value: (r) => r.balance_due.toFixed(2) },
          { label: 'Aging Bucket', value: (r) => r.bucket },
        ],
        rows
      )
    })

  const exportJobsCompleted = () =>
    runExport('jobs_completed', async () => {
      const { data, error } = await supabase
        .from('jobs')
        .select(
          'job_number, title, actual_amount, quoted_amount, actual_end_time, employee:employees(first_name, last_name), crew:crews(name), customer:customers(company_name)'
        )
        .eq('status', 'completed')
        .gte('actual_end_time', dateRange.start)
        .lte('actual_end_time', `${dateRange.end}T23:59:59`)
      if (error) throw error

      const rows = (data || []).map((job: any) => ({
        job_number: job.job_number,
        title: job.title,
        customer: job.customer?.company_name || '',
        employee: job.employee ? `${job.employee.first_name || ''} ${job.employee.last_name || ''}`.trim() : '',
        crew: job.crew?.name || '',
        amount: Number(job.actual_amount || job.quoted_amount || 0),
        completed_at: job.actual_end_time,
      }))

      exportRowsToCsv(
        `jobs-completed_${dateRange.start}_${dateRange.end}.csv`,
        [
          { label: 'Job #', value: (r) => r.job_number },
          { label: 'Title', value: (r) => r.title },
          { label: 'Customer', value: (r) => r.customer },
          { label: 'Employee', value: (r) => r.employee },
          { label: 'Crew', value: (r) => r.crew },
          { label: 'Amount', value: (r) => r.amount.toFixed(2) },
          { label: 'Completed At', value: (r) => r.completed_at },
        ],
        rows
      )
    })

  const exportEmployeeHours = () =>
    runExport('employee_hours', async () => {
      const { data, error } = await supabase
        .from('time_entries')
        .select('total_hours, is_approved, clock_in_time, entry_type, employee:employees(first_name, last_name), job:jobs(operational_division_id, service_type)')
        .gte('clock_in_time', `${dateRange.start}T00:00:00`)
        .lte('clock_in_time', `${dateRange.end}T23:59:59`)
      if (error) throw error

      const byEmployee = new Map<
        string,
        { hours: number; approvedHours: number; travelHours: number; jobHours: number; entries: number }
      >()
      for (const entry of ((data || []) as any[]).filter((row) => {
        if (row.entry_type === 'day' || row.entry_type === 'travel' || !row.job) return true
        const job = Array.isArray(row.job) ? row.job[0] || null : row.job
        return matchesDivisionWithFallback(job, currentDivision)
      })) {
        const name = entry.employee
          ? `${entry.employee.first_name || ''} ${entry.employee.last_name || ''}`.trim()
          : 'Unassigned'
        const existing = byEmployee.get(name) || {
          hours: 0,
          approvedHours: 0,
          travelHours: 0,
          jobHours: 0,
          entries: 0,
        }
        const hours = Number(entry.total_hours || 0)
        // Day clock spans the whole day; don't double-count into total billable/worked rollups
        if (entry.entry_type !== 'day') {
          existing.hours += hours
          if (entry.is_approved) existing.approvedHours += hours
        }
        if (entry.entry_type === 'travel') existing.travelHours += hours
        if (entry.entry_type === 'job' || (!entry.entry_type && entry.job)) existing.jobHours += hours
        existing.entries += 1
        byEmployee.set(name, existing)
      }
      const rows = Array.from(byEmployee.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([employee, v]) => ({ employee, ...v }))

      exportRowsToCsv(
        `employee-hours_${dateRange.start}_${dateRange.end}.csv`,
        [
          { label: 'Employee', value: (r) => r.employee },
          { label: 'Entries', value: (r) => r.entries },
          { label: 'Job Hours', value: (r) => r.jobHours.toFixed(2) },
          { label: 'Travel Hours', value: (r) => r.travelHours.toFixed(2) },
          { label: 'Total Hours', value: (r) => r.hours.toFixed(2) },
          { label: 'Approved Hours', value: (r) => r.approvedHours.toFixed(2) },
        ],
        rows
      )
    })

  const exportContractStatusSummary = () =>
    runExport('contract_status', async () => {
      const { data, error } = await supabase
        .from('contracts')
        .select('status, contract_total_amount, total_amount')
      if (error) throw error

      const byStatus = new Map<string, { count: number; total: number }>()
      for (const c of data || []) {
        const status = c.status || 'unknown'
        const existing = byStatus.get(status) || { count: 0, total: 0 }
        existing.count += 1
        existing.total += Number(c.contract_total_amount || c.total_amount || 0)
        byStatus.set(status, existing)
      }
      const rows = Array.from(byStatus.entries())
        .sort((a, b) => b[1].total - a[1].total)
        .map(([status, v]) => ({ status, ...v }))

      exportRowsToCsv(
        `contract-status-summary_${new Date().toISOString().split('T')[0]}.csv`,
        [
          { label: 'Status', value: (r) => r.status },
          { label: 'Contracts', value: (r) => r.count },
          { label: 'Total Value', value: (r) => r.total.toFixed(2) },
        ],
        rows
      )
    })

  const csvExports = [
    { key: 'revenue', name: 'Revenue by Period', description: 'Invoiced and collected revenue grouped by month', icon: DollarSignIcon, run: exportRevenueByPeriod },
    { key: 'ar_aging', name: 'AR Aging', description: 'Outstanding invoice balances by aging bucket', icon: AlertCircleIcon, run: exportArAging },
    { key: 'jobs_completed', name: 'Jobs Completed', description: 'Completed jobs by employee/crew', icon: CheckCircleIcon, run: exportJobsCompleted },
    { key: 'employee_hours', name: 'Employee Hours', description: 'Time entry hours by employee', icon: ClockIcon, run: exportEmployeeHours },
    { key: 'contract_status', name: 'Contract Status Summary', description: 'Contract counts and value by status', icon: FileTextIcon, run: exportContractStatusSummary },
  ]

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const reportCategories = [
    {
      title: 'Financial Reports',
      description: 'Revenue, expenses, and profitability analysis',
      icon: DollarSignIcon,
      color: 'blue',
      reports: [
        {
          name: 'Profit & Loss Statement',
          description: 'Comprehensive P&L analysis',
          route: '/pnl-dashboard',
          icon: TrendingUpIcon,
        },
        {
          name: 'Job Costing',
          description: 'Materials, garbage bags, and amounts by job',
          route: '/reports/builder',
          icon: WrenchIcon,
        },
        {
          name: 'Revenue Summary',
          description: 'Monthly and yearly revenue trends',
          route: '/billing',
          icon: BarChart3Icon,
        },
        {
          name: 'Customer Analysis',
          description: 'Customer profitability and trends',
          route: '/customers',
          icon: UsersIcon,
        },
      ],
    },
    {
      title: 'Operational Reports',
      description: 'Job performance and operational metrics',
      icon: CheckCircleIcon,
      color: 'green',
      reports: [
        {
          name: 'Daily Dispatch',
          description: 'Daily job assignments and schedules',
          route: '/job-calendar',
          icon: CalendarIcon,
        },
        {
          name: 'Worker Schedule',
          description: 'Jobs per worker, property, and period with status',
          route: '/reports/schedule',
          icon: CalendarIcon,
        },
        {
          name: 'Garbage Jobs After 6 PM',
          description: 'DSNY jobs completed after 6:00 PM, per worker',
          route: '/reports/builder?preset=dsny-after-6pm',
          icon: ClockIcon,
        },
        {
          name: 'Incomplete Jobs',
          description: 'Jobs not completed, per worker and address',
          route: '/reports/builder?preset=incomplete-jobs',
          icon: AlertCircleIcon,
        },
        {
          name: 'End of Day Summary',
          description: 'Daily operational summary',
          route: '/end-of-day',
          icon: ClockIcon,
        },
        {
          name: 'Job Performance',
          description: 'Job completion rates and efficiency',
          route: '/jobs',
          icon: CheckCircleIcon,
        },
      ],
    },
    {
      title: 'Quality Control',
      description: 'QC reports and compliance tracking',
      icon: AlertCircleIcon,
      color: 'yellow',
      reports: [
        {
          name: 'QC Schedule Report',
          description: 'Quality control scheduling',
          route: '/qc-schedule',
          icon: CalendarIcon,
        },
        {
          name: 'QC Visit Results',
          description: 'QC visit outcomes and findings',
          route: '/qc-visits',
          icon: CheckCircleIcon,
        },
        {
          name: 'Compliance Report',
          description: 'Regulatory compliance tracking',
          route: '/reports',
          icon: FileTextIcon,
        },
      ],
    },
  ]

  const quickStats = [
    {
      title: 'Total Revenue',
      value: formatCurrency(stats.totalRevenue),
      change: '',
      changeType: 'positive' as const,
      icon: DollarSignIcon,
      color: 'green',
    },
    {
      title: 'Jobs Completed (This Month)',
      value: stats.jobsCompletedThisMonth.toString(),
      change: '',
      changeType: 'positive' as const,
      icon: CheckCircleIcon,
      color: 'blue',
    },
    {
      title: 'Active Customers',
      value: stats.totalCustomers.toString(),
      change: '',
      changeType: 'positive' as const,
      icon: UsersIcon,
      color: 'purple',
    },
    {
      title: 'Active Contracts',
      value: stats.activeContracts.toString(),
      change: '',
      changeType: 'positive' as const,
      icon: FileTextIcon,
      color: 'orange',
    },
  ]

  const getColorClasses = (color: string) => {
    const colors = {
      blue: 'bg-blue-50 text-blue-600',
      green: 'bg-green-50 text-green-600',
      yellow: 'bg-yellow-50 text-yellow-600',
      purple: 'bg-purple-50 text-purple-600',
      orange: 'bg-orange-50 text-orange-600',
    }
    return colors[color as keyof typeof colors] || colors.blue
  }

  return (
    <MobilePage>
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
        <p className="text-gray-600">Comprehensive reporting and business intelligence</p>
      </div>

      {/* Date Range Selector */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Report Period</h2>
          <div className="flex flex-col sm:flex-row gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleApplyFilter}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Loading...' : 'Apply Filter'}
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {loading ? (
            <div className="col-span-4 text-center py-8">
              <p className="text-gray-600">Loading statistics...</p>
            </div>
          ) : (
            quickStats.map((stat, index) => (
              <div key={index} className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="mt-2 text-3xl font-bold text-gray-900">{stat.value}</p>
                    {stat.change && (
                      <p
                        className={`mt-2 text-sm ${
                          stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {stat.change} from last period
                      </p>
                    )}
                  </div>
                <div className={`p-3 rounded-lg ${getColorClasses(stat.color)}`}>
                  <stat.icon className="w-6 h-6" />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Report Categories */}
      <div className="space-y-8">
        {reportCategories.map((category, categoryIndex) => (
          <div key={categoryIndex} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className={`p-3 rounded-lg ${getColorClasses(category.color)}`}>
                  <category.icon className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{category.title}</h2>
                  <p className="text-gray-600">{category.description}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {category.reports.map((report, reportIndex) => (
                  <button
                    key={reportIndex}
                    onClick={() => navigate(report.route)}
                    className="group p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all text-left"
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-gray-50 rounded-lg group-hover:bg-blue-50 transition-colors">
                        <report.icon className="w-5 h-5 text-gray-600 group-hover:text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600">
                          {report.name}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">{report.description}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

      {/* Export Reports */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Export Reports</h2>
                <p className="text-sm text-gray-500">CSV exports honoring the report period above</p>
              </div>
              <button
                onClick={() => navigate('/reports/builder')}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-50"
              >
                <WrenchIcon className="w-4 h-4" />
                Custom Report Builder
              </button>
            </div>
          </div>

          {exportError && (
            <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-800 text-sm">{exportError}</p>
            </div>
          )}

          <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {csvExports.map((item) => (
              <div key={item.key} className="border border-gray-200 rounded-lg p-4 flex items-start gap-3">
                <div className="p-2 bg-gray-50 rounded-lg">
                  <item.icon className="w-5 h-5 text-gray-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-gray-900">{item.name}</h3>
                  <p className="text-xs text-gray-500 mt-1 mb-3">{item.description}</p>
                  <button
                    onClick={item.run}
                    disabled={exporting === item.key}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {exporting === item.key ? (
                      <Loader2Icon className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <DownloadIcon className="w-3.5 h-3.5" />
                    )}
                    Export CSV
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
    </MobilePage>
  )
}
